import { useState, useEffect } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import {
  ArrowLeft,
  Calendar,
  Image as ImageIcon,
  RefreshCw,
  Target,
  Trash2,
  Zap,
} from "lucide-react"
import {
  deleteUser,
  extendSeason as extendSeasonApi,
  getUserProfile,
  getUserChatAttachments,
  loadSeason,
  regenSeasonMagnaCartaV,
  type UserChatAttachment,
} from "@/lib/api"
import { useWorkoutStore } from "@/stores/workoutStore"
import { SafetyDialog } from "@/components/ConfirmDialog"
import { UserProfileCard } from "@/components/user-profile-card"
import { FullProfilePanel } from "@/components/full-profile-panel"
import { GoalsPanel } from "@/components/GoalsPanel"
import { WeekCalendar } from "@/components/WeekCalendar"
import { GenerateSeasonButton } from "@/components/GenerateSeasonButton"
import { ProfileEditor } from "@/components/ProfileEditor"
import { toast } from "sonner"
import type { Season } from "@/types/workout"
import { walkSeasonWeeks } from "@/types/workout"

function FilesPanel({ userId }: { userId: string }) {
  const [images, setImages] = useState<UserChatAttachment[] | null>(null)
  const [count, setCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError(null)
    getUserChatAttachments(userId)
      .then((res) => {
        if (cancelled) return
        setImages(res.images)
        setCount(res.count)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : "Failed to load files")
      })
      .finally(() => {
        if (cancelled) return
        setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  // Group by conversation_id so admins can scan per chat thread. Files at the
  // bucket root (conversation_id == null) get bucketed under "(no conversation)".
  const grouped = (images ?? []).reduce<Record<string, UserChatAttachment[]>>(
    (acc, img) => {
      const key = img.conversation_id ?? "(no conversation)"
      ;(acc[key] ||= []).push(img)
      return acc
    },
    {},
  )

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ImageIcon className="size-4 text-muted-foreground" />
          <h3 className="font-semibold">Files</h3>
          {!isLoading && !error && (
            <span className="text-xs text-muted-foreground">
              {count} image{count === 1 ? "" : "s"} from chat
            </span>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="aspect-square bg-muted animate-pulse rounded-md"
            />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
          {error}
        </div>
      )}

      {!isLoading && !error && count === 0 && (
        <p className="text-sm text-muted-foreground">
          This user hasn't sent any images via chat.
        </p>
      )}

      {!isLoading && !error && count > 0 && (
        <div className="space-y-4">
          {Object.entries(grouped).map(([conversationId, items]) => (
            <div key={conversationId} className="space-y-1.5">
              <div className="flex items-baseline gap-2 text-xs text-muted-foreground">
                <span className="font-mono">{conversationId}</span>
                <span>·</span>
                <span>
                  {items.length} image{items.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {items.map((img) => (
                  <a
                    key={img.path}
                    href={img.signed_url ?? "#"}
                    target="_blank"
                    rel="noreferrer"
                    title={`${img.filename}${img.size_bytes ? ` · ${Math.round(img.size_bytes / 1024)} KB` : ""}`}
                    className="group relative aspect-square overflow-hidden rounded-md border bg-muted"
                  >
                    {img.signed_url ? (
                      <img
                        src={img.signed_url}
                        alt={img.filename}
                        loading="lazy"
                        className="size-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="size-full flex items-center justify-center text-[10px] text-muted-foreground">
                        signing failed
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

import { ArchetypeRow } from "@/components/ArchetypeRow"

function SeasonSummaryCard({
  season,
  profile,
  userId,
}: {
  season: Season
  profile: Record<string, unknown> | null
  userId: string
}) {
  const totalWeeks = season.cycles.reduce(
    (sum, c) => sum + c.weeks.length,
    0,
  )
  const allDates = season.cycles.flatMap((c) =>
    c.weeks.flatMap((w) => Object.keys(w.days)),
  )
  const sorted = allDates.sort()
  const firstDate = sorted[0]
  const lastDate = sorted[sorted.length - 1]

  const allDays = season.cycles.flatMap((c) =>
    c.weeks.flatMap((w) => Object.values(w.days)),
  )
  const workoutDays = allDays.filter(
    (d) => !(d as unknown as Record<string, unknown>).rest_day,
  ).length
  const restDays = allDays.length - workoutDays

  // The user's selected goals are what we want to show here — NOT the
  // AI-emitted season.goal (which is always something generic like
  // "Helix Training Season"). Reach into the profile for them.
  const profileGoals = formatProfileGoals(
    (profile?.goals as unknown) ?? null,
  )

  return (
    <div className="rounded-lg border bg-card p-4 space-y-2">
      <div className="flex items-center gap-2">
        <Target className="size-4 text-muted-foreground" />
        <h3 className="font-semibold">Season Summary</h3>
      </div>
      <div className="text-sm">
        <span className="text-muted-foreground">User goals: </span>
        <span className="font-medium">{profileGoals ?? "—"}</span>
      </div>
      <ArchetypeRow userId={userId} />
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm pt-1 border-t mt-2">
        <div>
          <span className="text-muted-foreground">Cycles: </span>
          <span className="font-medium">{season.cycles.length}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Weeks: </span>
          <span className="font-medium">{totalWeeks}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Total Days: </span>
          <span className="font-medium">{allDates.length}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Workout / Rest: </span>
          <span className="font-medium">
            {workoutDays} / {restDays}
          </span>
        </div>
        <div className="col-span-2">
          <span className="text-muted-foreground">Date Range: </span>
          <span className="font-medium">
            {firstDate && lastDate
              ? `${new Date(firstDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${new Date(lastDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
              : "—"}
          </span>
        </div>
      </div>
      {season.priorities && season.priorities.length > 0 && (
        <div className="text-sm">
          <span className="text-muted-foreground">Priorities: </span>
          <span>{season.priorities.join(", ")}</span>
        </div>
      )}
    </div>
  )
}

// Mirror of formatGoals in user-profile-card.tsx — kept local so this card
// doesn't pull the larger UserProfileCard component just for one helper. If
// the goal-label set grows we'll lift this to a shared formatter.
const _GOAL_TYPE_LABEL: Record<string, string> = {
  "gain-muscle": "Build muscle",
  "build-muscle": "Build muscle",
  "lose-fat": "Lose fat",
  "lose-body-fat": "Lose fat",
  "get-stronger": "Get stronger",
  "get-faster": "Get faster",
  "train-event": "Train for an event",
  "push-myself": "Push myself",
}

function formatProfileGoals(goals: unknown): string | null {
  if (!goals || !Array.isArray(goals) || goals.length === 0) return null
  const out: string[] = []
  for (const raw of goals) {
    if (typeof raw === "string") {
      out.push(raw)
      continue
    }
    const g = raw as { name?: string; type?: string; muscleGroups?: string[]; eventType?: string; targetBodyFat?: number; currentBodyFat?: number }
    const head = g.name ?? (g.type ? (_GOAL_TYPE_LABEL[g.type] ?? g.type) : null)
    if (!head) continue
    let detail: string | null = null
    if (g.muscleGroups && g.muscleGroups.length > 0) detail = g.muscleGroups.join("/")
    else if (g.eventType) detail = g.eventType
    else if (typeof g.targetBodyFat === "number") detail = `${g.currentBodyFat ?? "?"}%→${g.targetBodyFat}%`
    out.push(detail ? `${head} (${detail})` : head)
  }
  return out.length > 0 ? out.join(", ") : null
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<Record<string, unknown> | null>(null)
  const [season, setSeason] = useState<Season | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isExtending, setIsExtending] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showRegenDialog, setShowRegenDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const {
    setCurrentUser,
    setSeason: setStoreSeason,
    setCurrentWeekIndex,
    isDirty,
  } = useWorkoutStore()

  // Browser-level guard against closing/reloading the tab with unsaved edits.
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [isDirty])

  // Reusable guard: refuse to run a destructive (re-fetch) action if there
  // are uncommitted edits in the calendar.
  const confirmIfDirty = (message: string): boolean => {
    if (!isDirty) return true
    return window.confirm(message)
  }

  useEffect(() => {
    if (!id) return

    async function fetchData() {
      setIsLoading(true)
      setError(null)
      try {
        const [profileData, seasonData] = await Promise.all([
          getUserProfile(id!),
          loadSeason(id!),
        ])
        setProfile(profileData)
        setSeason(seasonData)

        if (profileData) {
          setCurrentUser({
            id: id!,
            first_name: profileData.first_name as string,
            last_name: profileData.last_name as string,
          })
        }
        if (seasonData) {
          setStoreSeason(seasonData)
          const today = new Date().toISOString().split("T")[0]
          const flattened = walkSeasonWeeks(seasonData)
          const idx = flattened.findIndex(({ week }) =>
            Object.keys(week.days).some((d) => d >= today),
          )
          setCurrentWeekIndex(
            idx >= 0 ? idx : Math.max(flattened.length - 1, 0),
          )
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load user data",
        )
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [id, setCurrentUser, setStoreSeason, setCurrentWeekIndex])

  const handleExtend = async () => {
    if (!id) return
    if (
      !confirmIfDirty(
        "You have unsaved workout edits. Extending will discard them. Continue?",
      )
    )
      return
    setIsExtending(true)
    try {
      const updated = await extendSeasonApi(id)
      setSeason(updated)
      setStoreSeason(updated)
      toast.success("Season extended")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to extend")
    } finally {
      setIsExtending(false)
    }
  }

  const handleRegen = async () => {
    if (!id) return
    if (
      !confirmIfDirty(
        "You have unsaved workout edits. Regenerating will discard them. Continue?",
      )
    )
      return
    setIsRegenerating(true)
    try {
      const result = await regenSeasonMagnaCartaV(id)
      const reloaded = await loadSeason(id)
      setSeason(reloaded)
      if (reloaded) setStoreSeason(reloaded)
      toast.success(
        `Regenerated via Magna Carta V — ${result.weeks_generated} week(s) rebuilt`,
      )
      setShowRegenDialog(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Regen failed")
    } finally {
      setIsRegenerating(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return
    setIsDeleting(true)
    try {
      const result = await deleteUser(id)
      const totalRows = Object.values(result.deleted_counts).reduce(
        (sum, n) => (typeof n === "number" && n > 0 ? sum + n : sum),
        0,
      )
      toast.success(
        `Deleted user (${totalRows} rows, auth_deleted=${result.auth_deleted})`,
      )
      setShowDeleteDialog(false)
      navigate("/users")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed")
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-6 w-32 bg-muted animate-pulse rounded" />
        <div className="h-32 bg-muted animate-pulse rounded-lg" />
        <div className="h-64 bg-muted animate-pulse rounded-lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <Link
          to="/users"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="size-4" /> Back to Users
        </Link>
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    )
  }

  const userName = profile
    ? `${profile.first_name} ${profile.last_name}`
    : "Unknown User"

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/users"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
          </Link>
          <h1 className="text-2xl font-bold">{userName}</h1>
          {isDirty && (
            <span
              title="You have unsaved workout edits"
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
            >
              <span className="size-1.5 rounded-full bg-amber-500" />
              Unsaved changes
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {season && (
            <button
              onClick={handleExtend}
              disabled={isExtending || isRegenerating || isDeleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
            >
              <Zap className="size-3.5" />
              {isExtending ? "Extending..." : "Extend Season"}
            </button>
          )}
          <button
            onClick={() => setShowRegenDialog(true)}
            disabled={isExtending || isRegenerating || isDeleting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-amber-400 text-amber-700 bg-amber-50 rounded-md hover:bg-amber-100 disabled:opacity-50"
            title="Rebuild this user's season through the Magna Carta pipeline"
          >
            <RefreshCw className="size-3.5" />
            {isRegenerating ? "Regenerating..." : "Regen Season"}
          </button>
          <button
            onClick={() => setShowDeleteDialog(true)}
            disabled={isExtending || isRegenerating || isDeleting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-400 text-red-700 bg-red-50 rounded-md hover:bg-red-100 disabled:opacity-50"
            title="Delete this user and every related row"
          >
            <Trash2 className="size-3.5" />
            {isDeleting ? "Deleting..." : "Delete User"}
          </button>
        </div>
      </div>

      {showRegenDialog && id && (
        <SafetyDialog
          title="Regenerate season"
          description={`Rebuild ${userName}'s season through the new agentic pipeline. A pre-migration snapshot is captured automatically; rollback via scripts/restore_from_snapshot.py ${id}.`}
          warnings={[
            "Current season will be replaced.",
            "If the output fails §13 validation, the old season is kept.",
            "Rate-limited to 10/min per admin.",
          ]}
          confirmLabel="Regenerate"
          variant="default"
          onConfirm={handleRegen}
          onCancel={() => setShowRegenDialog(false)}
          isLoading={isRegenerating}
        />
      )}

      {showDeleteDialog && id && (
        <SafetyDialog
          title="Delete user"
          description={`Permanently delete ${userName} and every row keyed to their user_id across profiles, seasons, conversations, wearable_* tables, and bookings. The auth.users row is also removed so the email/phone is free to re-register.`}
          warnings={[
            "A pre-delete snapshot is captured to _snapshots/pre_delete/ for rollback.",
            "This action is not reversible without running the restore script.",
          ]}
          confirmLabel="Delete user"
          variant="destructive"
          typeToConfirm={id}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteDialog(false)}
          isLoading={isDeleting}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {profile && id && (
          <div className="space-y-2">
            <UserProfileCard profile={profile} />
            <div className="flex justify-end">
              <ProfileEditor
                userId={id}
                profile={profile}
                onSaved={(updated) => {
                  setProfile(updated)
                  if (updated.first_name || updated.last_name) {
                    setCurrentUser({
                      id,
                      first_name: updated.first_name as string,
                      last_name: updated.last_name as string,
                    })
                  }
                }}
              />
            </div>
          </div>
        )}
        {season && id && (
          <SeasonSummaryCard season={season} profile={profile} userId={id} />
        )}
      </div>

      {profile && <FullProfilePanel profile={profile} />}

      {/* Goal Target-Time (Story 1.9): view + inline CRUD of the user's goals
          (goals/event_goals tables via goal_service) — the ops escape hatch. */}
      {id && <GoalsPanel userId={id} />}

      {id && <FilesPanel userId={id} />}

      {season ? (
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="size-4 text-muted-foreground" />
            <h3 className="font-semibold">Workout Plan</h3>
          </div>
          <WeekCalendar />
        </div>
      ) : (
        <div className="rounded-lg border bg-card p-12 text-center space-y-4">
          <p className="text-muted-foreground">
            No workout plan found for this user.
          </p>
          {id && (
            <GenerateSeasonButton
              userId={id}
              userLabel={userName}
              onGenerated={async () => {
                if (!id) return
                const reloaded = await loadSeason(id)
                setSeason(reloaded)
                if (reloaded) setStoreSeason(reloaded)
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
